// components/crm/ContactTypeManagement.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Spin
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ContactType } from '../../api/CRMApiClient';
import { useStore } from '../../stores/root-store-context';

const ContactTypeManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { crmStore } = useStore();

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<ContactType | null>(null);
  const [form] = Form.useForm();

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Filters                                                                 */
  /* ───────────────────────────────────────────────────────────────────────── */
  const [search, setSearch] = useState<string>('');

  const filteredTypes = useMemo(() => {
    const q = search.toLowerCase();
    return q ? crmStore.contactTypes.filter((t) => t.name.toLowerCase().includes(q)) : crmStore.contactTypes;
  }, [crmStore.contactTypes, search]);

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Data loading                                                            */
  /* ───────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    crmStore.getContactTypes();
  }, [crmStore]);

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  CRUD helpers                                                            */
  /* ───────────────────────────────────────────────────────────────────────── */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModal(true);
  };

  const openEdit = (type: ContactType) => {
    setEditing(type);
    form.setFieldsValue(type);
    setModal(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    if (editing) {
      await crmStore.updateContactType(editing.id, values);
    } else {
      await crmStore.addContactType(values);
    }
    setModal(false);
  };

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Table columns                                                           */
  /* ───────────────────────────────────────────────────────────────────────── */
  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: '10%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '70%' },
    {
      title: t('Actions'),
      key: 'actions',
      width: '20%',
      render: (_: unknown, record: ContactType) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title={t('DeleteContactTypeConfirm')}
            onConfirm={() => crmStore.deleteContactType(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Render                                                                  */
  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <Spin spinning={crmStore.isLoading}>
      <Card
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            {t('AddContactType')}
          </Button>
        }
      >
        {/* Search filter */}
        <Input.Search
          placeholder={t('Search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 240, marginBottom: 16 }}
        />

        <Table dataSource={filteredTypes} columns={columns} rowKey="id" pagination={false} />

        {/* Modal */}
        <Modal
          title={editing ? t('EditContactType') : t('AddContactType')}
          open={modal}
          onOk={save}
          onCancel={() => setModal(false)}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label={t('Name')}
              rules={[{ required: true, message: t('PleaseEnterName') }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </Spin>
  );
});

export default ContactTypeManagement;
